// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// O DIA SUBCONTRATADO E A VIAGEM A QUE PERTENCE — para se poder SALTAR para lá
// ============================================================================
//
// 📗 Cliente: *«Os dias subcontratados, de forma a ser mais fácil a visualização, têm de estar
//    ligados à viagem a que estão relacionados, nos detalhes, para saltar para a viagem, de
//    forma a ser mais facilmente perceptível.»*
//
// 📌 **O gesto já existe e chama-se «Ver no mapa»** — o `irPara` do `Mapa`, com as réguas que
//    se cruzam no alvo e saem ao fim de 9 s. Isto não desenha salto nenhum: **calcula para
//    onde saltar**, e entrega ao gesto que já lá está.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ A PERGUNTA A RESPONDER PRIMEIRO NÃO É «COMO SALTAR» — É «HÁ PARA ONDE?»
// ---------------------------------------------------------------------------
//
//   Um botão «ver a viagem» que aterra em lado nenhum é indistinguível de um botão avariado, e
//   este projecto já produziu esse defeito três vezes — a versão mais crua foi um
//   `if (alt.viaturaId === null) return;`, um `return` silencioso com nenhum erro a explicá-lo.
//   📌 Por isso esta função devolve **a lista de destinos**, que pode ser **vazia**, e quem a
//   usa tem de dizer isso por palavras em vez de mostrar um botão morto.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ E A SEGUNDA É SE O DESTINO É ÚNICO — as DUAS ambiguidades, e as duas são reais
// ---------------------------------------------------------------------------
//
//   1 · **NA ORIGEM.** Um subcontratado é uma EMPRESA e pode ter **três serviços no mesmo
//       dia** (D-002) — é a razão de a secção existir. «Saltar para a viagem» sobre um dia com
//       três não tem resposta única, logo devolvem-se **os três** e o ecrã pergunta qual.
//
//   2 · **NO DESTINO.** A mesma referência pode correr em **mais do que um autocarro** (F-22).
//       ⚠️ Medido na base do `dev` a 2026-09-02 (`sonda-o-dia-subcontratado-e-a-viagem.mjs`):
//       **629 de 6 259 referências (10,0 %) estão em mais de uma viatura**, com o pior caso em
//       **3**. Escolher uma por nós mandava o olho para o autocarro errado em uma de cada dez.
//
//   📌 **Em nenhum dos dois se escolhe por omissão.** A regra deste projecto é a mesma que já
//   se aplicou ao 409 `rotacao_ambigua`: quando há duas leituras legítimas, nomeiam-se as duas.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ OS DOIS TIPOS DE SUBCONTRATADO — a ressalva do cliente, e o que se PODE dizer
// ---------------------------------------------------------------------------
//
//   📗 *«existem os subcontratos LOCAIS e de SERVIÇO, por isso a lógica é ligeiramente
//   diferente»* — e é verdade, com números (migração 031, medidos na folha real):
//
//     · **parceiro LOCAL** (por km) — o pool; **42,4 % dos serviços são de UM dia**. É o dia
//       solto contratado dentro de uma viagem que corre na nossa frota → o salto tem quase
//       sempre destino, e é aqui que ele vale mais.
//     · **parceiro de SERVIÇO** (por dia) — os parceiros nomeados; *«comportam-se como
//       autocarros»*, 1,1 % de um dia. A viagem pode ser inteira deles → sem destino nenhum na
//       frota, e a resposta certa é dizê-lo.
//
//   ⚠️⚠️ **MAS `subcontratado.tipo` NÃO CHEGA A ESTE ECRÃ.** `construirDadosMapa` reduz cada
//   empresa a `{ id, nome, base }` e deita fora o `tipo`, que existe no CSV e na base. 📌 A
//   diferença que o cliente descreve **manifesta-se na contagem de destinos**, que é calculável
//   aqui — «tem viatura nossa» contra «não tem» —, e é por isso que esta função não precisa do
//   `tipo` para se comportar bem nos dois casos. O que **não** se pode fazer sem ele é
//   *nomear* o tipo no ecrã; fica declarado.
// ============================================================================

import type { CelulaCompacta, DadosMapa } from '@xlv-gva-mpt/dominio';
import { extentoDoServico } from './barras-do-subcontratado.js';

/** Uma viatura nossa que leva esta mesma viagem — o destino do salto. */
export interface DestinoDaViagem {
  /** Índice da linha na grelha da frota. */
  readonly viatura: number;
  /** O `id` da viatura, que é o que o `irPara` do mapa recebe. */
  readonly id: string;
  /** O número interno, que é o que o planeador conhece. */
  readonly numero: string;
  readonly dia0: number;
  readonly dia1: number;
  /**
   * Anda ao mesmo tempo que o serviço subcontratado, ou vem antes/depois?
   *
   * ⚠️ São duas coisas diferentes e o ecrã tem de as distinguir: **ao mesmo tempo** é o grupo
   * que não cabe num autocarro e leva reforço de fora; **noutros dias** é a empresa a cobrir
   * um pedaço da viagem (uma rendição). A mesma distinção que `outrasViaturasDaViagem` faz
   * para a frota.
   */
  readonly simultanea: boolean;
}

/** Um serviço subcontratado num dia, e para onde ele liga. */
export interface ViagemDoSubcontratado {
  /** Índice do serviço dentro do dia — a chave estável para o ecrã. */
  readonly indiceNoDia: number;
  /** `-1` quando o serviço não tem corrida: então não há viagem a que ligar. */
  readonly corrida: number;
  /** Vazio quando não há corrida. */
  readonly referencia: string;
  readonly clienteSigla: string;
  /** Os dias deste serviço subcontratado. */
  readonly d0: number;
  readonly d1: number;
  /** ⚠️ Pode ser **vazio**: a viagem corre só nesta empresa. Ver o cabeçalho. */
  readonly destinos: readonly DestinoDaViagem[];
}

/**
 * Os dias em que a viatura `v` leva a corrida `corrida`, ou `null` se não a leva.
 *
 * ⚠️⚠️ **VARRE A LINHA INTEIRA e não o bloco contíguo**, e a razão não é minha: é a mesma que
 * `outrasViaturasDaViagem` escreve no domínio — uma corrida pode ser interrompida (a viatura
 * larga o grupo, faz outra coisa, volta a apanhá-lo), e parar no primeiro buraco anunciava um
 * intervalo mais curto do que o real.
 *
 * ⚠️ **É a única coisa que aqui se repete do domínio**, e repete-se porque
 * `outrasViaturasDaViagem` entra por um **índice de viatura** e aqui não há nenhum: o serviço
 * está na secção dos subcontratados. 📌 Para que as duas não divirjam — *«a formulação que
 * divergir é a que ninguém corre»* — há um critério em `viagem-do-subcontratado.test.ts` que
 * exige que as duas encontrem exactamente o mesmo conjunto de viaturas para a mesma corrida.
 */
function diasDaCorrida(
  grelha: readonly (readonly (CelulaCompacta | null)[])[],
  v: number,
  corrida: number,
): { readonly dia0: number; readonly dia1: number } | null {
  const linha = grelha[v] ?? [];
  let dia0 = -1;
  let dia1 = -1;
  for (let d = 0; d < linha.length; d++) {
    if ((linha[d]?.[10] ?? -1) !== corrida) continue;
    if (dia0 < 0) dia0 = d;
    dia1 = d;
  }
  return dia0 < 0 ? null : { dia0, dia1 };
}

/**
 * Os serviços que `linha` tem no dia `dia`, cada um com as viaturas nossas que levam a mesma
 * viagem.
 *
 * ⚠️⚠️ **RECEBE A LINHA E NÃO O ÍNDICE DA EMPRESA — é o C₂.** Com uma linha por serviço, quem
 * pergunta pergunta sobre **um** serviço, e passar o índice da empresa devolvia os **nove** do
 * dia sob o nome de um deles. 📌 A função não muda de comportamento: continua a mapear os
 * serviços do dia daquela grelha — só que a grelha de uma linha tem, por construção, um.
 * Continua a aceitar a grelha de uma EMPRESA inteira, que é o que os critérios usam para provar
 * que o caso de vários serviços num dia continua a ser tratado.
 *
 * Devolve vazio quando o dia não tem serviço nenhum — e então não há nada a ligar.
 */
export function viagensDoSubcontratado(
  dados: DadosMapa,
  linha: readonly (readonly CelulaCompacta[])[],
  dia: number,
): readonly ViagemDoSubcontratado[] {
  const servicos = linha[dia] ?? [];
  return servicos.map((c, indiceNoDia) => {
    const { d0, d1 } = extentoDoServico(linha, dia, indiceNoDia);
    const corrida = c[10];
    const destinos: DestinoDaViagem[] = [];
    if (corrida >= 0) {
      for (let v = 0; v < dados.grelha.length; v++) {
        const dela = diasDaCorrida(dados.grelha, v, corrida);
        if (dela === null) continue;
        destinos.push({
          viatura: v,
          id: dados.viaturas[v]?.id ?? '',
          numero: dados.viaturas[v]?.numero ?? String(v),
          dia0: dela.dia0,
          dia1: dela.dia1,
          // Sobreposição de intervalos, contra os dias do serviço SUBCONTRATADO.
          simultanea: dela.dia0 <= d1 && d0 <= dela.dia1,
        });
      }
    }
    return {
      indiceNoDia,
      corrida,
      referencia: corrida >= 0 ? (dados.corridas[corrida] ?? '') : '',
      clienteSigla: c[0] >= 0 ? (dados.clientes[c[0]]?.[0] ?? '') : '',
      d0,
      d1,
      // A que anda ao mesmo tempo primeiro: é a que tem consequência imediata — a mesma
      // ordenação de `outrasViaturasDaViagem`.
      destinos: destinos.sort(
        (a, b) => Number(b.simultanea) - Number(a.simultanea) || a.dia0 - b.dia0,
      ),
    };
  });
}
